/**
 * `LeninkaArt.Deposits.DepositRecordStatus` — совпадает с backend-ui (`src/api/deposits/types.ts`).
 * Черновик в UI backend-ui: `DEPOSIT_UI_DRAFT_STATUS = -1`, не часть этого enum.
 */
export type DepositRecordStatus = 2 | 3 | 4 | 5 | 6

export const DEPOSIT_RECORD_STATUSES: readonly DepositRecordStatus[] = [2, 3, 4, 5, 6]

export const isDepositRecordStatus = (n: number): n is DepositRecordStatus =>
  n === 2 || n === 3 || n === 4 || n === 5 || n === 6

/** Именованные коды для читаемости (как enum в backend-ui). */
export const DepositRecordStatusCode = {
  PendingModeration: 2,
  Confirmed: 3,
  Rejected: 4,
  TeamReview: 5,
  NeedsRevision: 6,
} as const satisfies Record<string, DepositRecordStatus>

/** Совпадает с `DEPOSIT_UI_DRAFT_STATUS` в backend-ui; после отзыва с модерации заявка в кабинете в этом статусе. */
export const DEPOSIT_UI_DRAFT_STATUS = -1

/** Значения `Statuses` для вкладки «Черновики» в GET my-list. */
export const DEPOSIT_MY_LIST_DRAFT_TAB_STATUSES = [-1, 0, 1, 7] as const

/** Допустимые коды для фильтра `Statuses` в списке «мои заявки». */
export type MyDepositMyListStatusFilter =
  | DepositRecordStatus
  | (typeof DEPOSIT_MY_LIST_DRAFT_TAB_STATUSES)[number]

/**
 * Кабинет: черновик и промежуточные стадии подачи.
 * В т.ч. UI-код `-1` (см. `DEPOSIT_UI_DRAFT_STATUS` в backend-ui) и значения после отзыва с модерации.
 * `7` — фактический код с бэкенда после отзыва с модерации в ряде ответов; в UI трактуем как черновик.
 * `Number()` — на случай строки из JSON.
 */
export const isDepositDraftOrPendingStatus = (n: number): boolean => {
  const v = Math.trunc(Number(n))
  return v === DEPOSIT_UI_DRAFT_STATUS || v === 0 || v === 1 || v === 7
}

/**
 * Разрешено ли владельцу удалить запись заявки (`DELETE …/intellectual-deposit/{id}`).
 * Консервативно: только «черновичные» стадии кабинета (как `isDepositDraftOrPendingStatus`).
 */
export const canDeleteIntellectualDepositApplication = (status: number): boolean =>
  isDepositDraftOrPendingStatus(status)

/** `LeninkaArt.Deposits.DepositWorkCategory` (Swagger enum 0–19). */
export const DEPOSIT_WORK_CATEGORIES = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
] as const

export type DepositWorkCategoryValue = (typeof DEPOSIT_WORK_CATEGORIES)[number]

export const isDepositWorkCategory = (n: number): n is DepositWorkCategoryValue =>
  (DEPOSIT_WORK_CATEGORIES as readonly number[]).includes(n)

/** `LeninkaArt.Deposits.DepositIndustryDirection` (Swagger enum 0–6). */
export const DEPOSIT_INDUSTRY_DIRECTIONS = [0, 1, 2, 3, 4, 5, 6] as const

/** Ключи `data.depositWorkCategoryMember.*` в порядке enum 0..19 (совпадает с i18n). */
export const DEPOSIT_WORK_CATEGORY_MEMBER_KEYS = [
  "LiteraryWork",
  "DramaScript",
  "MusicalWork",
  "PhonogramRecording",
  "AudiovisualWork",
  "FineArt",
  "Handicraft",
  "PhotographyIllustration",
  "DesignLogo",
  "ArchitectureBlueprintPrototype",
  "ChoreographyDance",
  "DigitalService",
  "OnlineCourseEducationalMaterial",
  "ScientificWork",
  "ComputerGraphics3d",
  "GameGameDesign",
  "Database",
  "CompositeCollection",
  "DerivativeWork",
  "Other",
] as const

/** Ключи `data.depositIndustryDirectionMember.*` в порядке enum 0..6. */
export const DEPOSIT_INDUSTRY_DIRECTION_MEMBER_KEYS = [
  "TextAndMeaningIndustries",
  "VisualImageIndustries",
  "AudioAndSoundIndustries",
  "PerformingArtsIndustries",
  "DigitalInteractiveIndustries",
  "ScienceAndEducationIndustries",
  "CraftAndAppliedArtsIndustries",
] as const

export type DepositIndustryDirectionValue = (typeof DEPOSIT_INDUSTRY_DIRECTIONS)[number]

export const isDepositIndustryDirection = (n: number): n is DepositIndustryDirectionValue =>
  (DEPOSIT_INDUSTRY_DIRECTIONS as readonly number[]).includes(n)

/** `LeninkaArt.Deposits.DepositAuthorRole` (0 author, 1 co-author). */
export type DepositAuthorRoleValue = 0 | 1

/** `LeninkaArt.Deposits.DepositRightsHolderKind` (0 individual, 1 organization). */
export type DepositRightsHolderKindValue = 0 | 1

/** Публичная карточка автора (без id). */
export type PublicDepositAuthorDto = {
  fullName?: string | null
  pseudonym?: string | null
}

/** Публичная карточка правообладателя (без чувствительных полей). */
export type PublicDepositRightsHolderDto = {
  kind?: number | null
  fullName?: string | null
  organizationName?: string | null
  sharePercent?: number | null
}

/** `LeninkaArt.Deposits.DepositAuthorDto` — полный автор на кабинет/модерацию. */
export type DepositAuthorDto = {
  id?: string | null
  fullName?: string | null
  pseudonym?: string | null
  countryId?: string | null
  role?: DepositAuthorRoleValue | number | null
  linkedPlatformUserId?: string | null
}

/** `LeninkaArt.Deposits.DepositRightsHolderDto` — полный правообладатель на кабинет/модерацию. */
export type DepositRightsHolderDto = {
  id?: string | null
  kind?: DepositRightsHolderKindValue | number | null
  fullName?: string | null
  countryId?: string | null
  organizationName?: string | null
  inn?: string | null
  ogrn?: string | null
  contactPerson?: string | null
  sharePercent?: number | null
}

/** Строки черновика (см. Swagger `DepositDraftAuthorDto` / `DepositDraftRightsHolderDto`). */
export type DepositDraftAuthorInputDto = {
  id?: string | null
  fullName?: string | null
  pseudonym?: string | null
  countryId?: string | null
  role?: DepositAuthorRoleValue | number | null
  linkedPlatformUserId?: string | null
}

export type DepositDraftRightsHolderInputDto = {
  id?: string | null
  kind?: DepositRightsHolderKindValue | number | null
  fullName?: string | null
  countryId?: string | null
  organizationName?: string | null
  inn?: string | null
  ogrn?: string | null
  contactPerson?: string | null
  sharePercent?: number | null
}

/** Для обратной совместимости — простые варианты без лишних полей. */
export type DepositAuthorInputDto = Pick<DepositDraftAuthorInputDto, "fullName" | "pseudonym">
export type DepositRightsHolderInputDto = Pick<
  DepositDraftRightsHolderInputDto,
  "kind" | "fullName" | "organizationName" | "sharePercent"
>

/** `LeninkaArt.Deposits.StagedDepositFileDto`. */
export type StagedDepositFileDto = {
  id: string
  sha256Hex?: string | null
  originalFileName?: string | null
}

/** `LeninkaArt.Deposits.DepositFileDownloadResult` (base64 контент). */
export type DepositFileDownloadResult = {
  content?: string | null
  fileName?: string | null
  contentType?: string | null
}

/** `LeninkaArt.Deposits.IntellectualDepositDraftDto` — поля, нужные мастеру заявки. */
export type IntellectualDepositDraftDto = {
  id: string
  ownerUserId?: string | null
  currentStep?: number | null
  portfolioAchievementId?: string | null
  stagedFileId?: string | null
  stagedFile?: StagedDepositFileDto | null
  workTitle?: string | null
  workDescription?: string | null
  workCategory?: number | null
  industryDirection?: number | null
  workCreationDate?: string | null
  creationCountryId?: string | null
  creationCityId?: string | null
  consentPersonalDataAcceptedAt?: string | null
  consentDepositRulesAcceptedAt?: string | null
  consentUserTermsAcceptedAt?: string | null
  legalDocumentsVersion?: string | null
  authors?: DepositDraftAuthorInputDto[] | null
  rightsHolders?: DepositDraftRightsHolderInputDto[] | null
}

/** `LeninkaArt.Deposits.UpdateDepositDraftInput` — тело PUT черновика. */
export type UpdateDepositDraftInput = {
  currentStep?: number | null
  portfolioAchievementId?: string | null
  clearPortfolioAchievement?: boolean
  stagedFileId?: string | null
  clearStagedFile?: boolean
  workTitle?: string | null
  workDescription?: string | null
  workCategory?: number | null
  industryDirection?: number | null
  workCreationDate?: string | null
  creationCountryId?: string | null
  creationCityId?: string | null
  applyCreationCountry?: boolean
  applyCreationCity?: boolean
  clearWorkLocation?: boolean
  acceptAllConsents?: boolean | null
  authors?: DepositDraftAuthorInputDto[] | null
  rightsHolders?: DepositDraftRightsHolderInputDto[] | null
}

/** `LeninkaArt.Deposits.ActiveDraftLookupDto`. */
export type ActiveDraftLookupDto = {
  hasDraft: boolean
  draft?: IntellectualDepositDraftDto | null
}

/** Тело создания/обновления заявки (camelCase, как в ABP JSON). */
export type UpsertIntellectualDepositDto = {
  title?: string | null
  description?: string | null
  workCategory?: number | null
  industryDirection?: number | null
  workCreationDate?: string | null
  creationCountryId?: string | null
  authors?: DepositAuthorInputDto[] | null
  rightsHolders?: DepositRightsHolderInputDto[] | null
}

/** Полная запись для кабинета (GET по id с токеном). */
export type IntellectualDepositDto = {
  id: string
  ownerUserId?: string | null
  portfolioAchievementId?: string | null
  title?: string | null
  description?: string | null
  status: number
  workCategory?: number | null
  industryDirection?: number | null
  workCategoryDisplay?: string | null
  industryDirectionDisplay?: string | null
  workCreationDate?: string | null
  creationCountryId?: string | null
  creationCityId?: string | null
  originalFileName?: string | null
  contentType?: string | null
  sizeBytes?: number | null
  sha256Hex?: string | null
  certificateNumber?: string | null
  certifiedAt?: string | null
  /** Backend переиспользует это поле и для reject, и для request-revision. */
  rejectionReason?: string | null
  consentPersonalDataAcceptedAt?: string | null
  consentDepositRulesAcceptedAt?: string | null
  consentUserTermsAcceptedAt?: string | null
  legalDocumentsVersion?: string | null
  submittedAt?: string | null
  creationTime?: string | null
  lastModificationTime?: string | null
  authors?: DepositAuthorDto[] | null
  rightsHolders?: DepositRightsHolderDto[] | null
}

/** Нормализация статуса записи кабинета: бэкенд может отдать `7` вместо UI-черновика `-1` после отзыва с модерации. */
export const normalizeDepositCabinetRecordStatus = (row: IntellectualDepositDto): IntellectualDepositDto => {
  const v = Math.trunc(Number(row.status))
  if (v === 7) return { ...row, status: DEPOSIT_UI_DRAFT_STATUS }
  return row
}

/** `LeninkaArt.Deposits.DepositStatusHistoryDto`. */
export type DepositStatusHistoryDto = {
  id: string
  fromStatus?: number | null
  toStatus?: number | null
  comment?: string | null
  actorUserId?: string | null
  changedAtUtc?: string | null
}

/** `LeninkaArt.Deposits.DepositModerationActionDto`. */
export type DepositModerationActionDto = {
  id: string
  actionType?: string | null
  comment?: string | null
  actorUserId?: string | null
  createdAtUtc?: string | null
}

/** `LeninkaArt.Deposits.IntellectualDepositAdminDetailDto` — расширение DepositDto для модератора. */
export type IntellectualDepositAdminDetailDto = IntellectualDepositDto & {
  originalFileDownloadUrl?: string | null
  statusHistory?: DepositStatusHistoryDto[] | null
  moderationActions?: DepositModerationActionDto[] | null
  /** `creationCountry` / `creationCity` lookups возвращаются вложено. */
  creationCountry?: { id?: string | null; name?: string | null } | null
  creationCity?: { id?: string | null; name?: string | null } | null
}

/** `LeninkaArt.Deposits.IntellectualDepositPublicDto` */
export type IntellectualDepositPublicDto = {
  id: string
  title?: string | null
  /** Enum индекс; публичный API может отдавать без `*Display`. */
  workCategory?: number | null
  industryDirection?: number | null
  workCategoryDisplay?: string | null
  industryDirectionDisplay?: string | null
  authors?: PublicDepositAuthorDto[] | null
  rightsHolders?: PublicDepositRightsHolderDto[] | null
  workCreationDate?: string | null
  creationCountryId?: string | null
  status: number
  certificateNumber?: string | null
  certifiedAt?: string | null
}

export type PagedIntellectualDepositPublicResult = {
  items?: IntellectualDepositPublicDto[] | null
  totalCount: number
}

export type PagedIntellectualDepositResult = {
  items?: IntellectualDepositDto[] | null
  totalCount: number
}
